import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { addWeeks, format, parseISO } from 'date-fns';
import { ArrowLeftRight, HandHelping, RefreshCw, X } from 'lucide-react';
import { shiftApi, shiftMarketplaceApi } from '../services/api';
import { Shift, ShiftMarketplaceRequest } from '../types';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import { formatCompanyDate, formatCompanyDateTime } from '../utils/companyDateFormat';
import { ACTIVE_MARKETPLACE_STATUSES, trimmedMarketplaceComment } from '../utils/shiftMarketplacePending';
import {
  MarketplaceRequestDetails,
  marketplaceDefaultExpanded,
  marketplaceStatusMeta,
} from '../components/scheduling/MarketplaceRequestDetails';
import { appConfirm } from '../context/SystemDialogContext';

type MainTab = 'forme' | 'approvals';

const parseLocalDateTime = (dateTimeStr: string): Date => {
  if (!dateTimeStr) return new Date();
  try {
    const raw = dateTimeStr.trim().replace(/Z$/i, '');
    if (!raw.includes('+') && !raw.includes('-', 10)) {
      const [datePart, timePart] = raw.split('T');
      const [year, month, day] = datePart.split('-').map(Number);
      const [hour, minute, second] = (timePart || '00:00:00').split(':').map(Number);
      return new Date(year, month - 1, day, hour, minute, second || 0);
    }
    return parseISO(dateTimeStr);
  } catch {
    return new Date(dateTimeStr);
  }
};

const formatShiftRange = (start?: string, end?: string) => {
  if (!start || !end) return '—';
  const s = parseLocalDateTime(start);
  const e = parseLocalDateTime(end);
  return `${format(s, 'EEE')} ${formatCompanyDate(s)} ${format(s, 'HH:mm')} – ${format(e, 'HH:mm')}`;
};

const shiftOptionLabel = (s: Shift) => {
  const who = s.employee
    ? `${s.employee.firstName} ${s.employee.lastName}`
    : `Employee #${s.employeeId}`;
  return `${who} · ${formatShiftRange(s.startTime, s.endTime)}`;
};

const isFutureOfferShift = (shift: Shift) =>
  parseLocalDateTime(shift.startTime).getTime() > Date.now() + 2 * 60 * 60 * 1000;

const ShiftMarketplace: React.FC = () => {
  const { employee } = useAuth();
  const { hasPermission } = usePermissions();
  const canPropose = hasPermission('marketplace_proposals');
  const canApprove = hasPermission('scheduling');
  const myId = employee?.id;
  const [searchParams, setSearchParams] = useSearchParams();

  const initialTab: MainTab =
    searchParams.get('tab') === 'approvals' && canApprove
      ? 'approvals'
      : !canPropose && canApprove
        ? 'approvals'
        : 'forme';
  const [mainTab, setMainTab] = useState<MainTab>(initialTab);

  const [loading, setLoading] = useState(true);
  const [staffRows, setStaffRows] = useState<ShiftMarketplaceRequest[]>([]);
  const [approvalRows, setApprovalRows] = useState<ShiftMarketplaceRequest[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const [createKind, setCreateKind] = useState<null | 'Giveaway' | 'Swap'>(null);
  const [myShifts, setMyShifts] = useState<Shift[]>([]);
  const [loadingMyShifts, setLoadingMyShifts] = useState(false);
  const [selectedOfferShiftId, setSelectedOfferShiftId] = useState<number | ''>('');
  const [peerShifts, setPeerShifts] = useState<Shift[]>([]);
  const [loadingPeers, setLoadingPeers] = useState(false);
  const [swapTargetId, setSwapTargetId] = useState<number | ''>('');
  const [creating, setCreating] = useState(false);
  const [createNotes, setCreateNotes] = useState('');
  const [actionNotes, setActionNotes] = useState<Record<number, string>>({});
  const [approvalNotes, setApprovalNotes] = useState<Record<number, string>>({});

  useEffect(() => {
    if (!canApprove && mainTab === 'approvals') setMainTab('forme');
  }, [canApprove, mainTab]);

  useEffect(() => {
    const q = searchParams.get('tab');
    if (q === 'approvals' && canApprove) setMainTab('approvals');
  }, [searchParams, canApprove]);

  const selectMainTab = (tab: MainTab) => {
    setMainTab(tab);
    if (tab === 'approvals') setSearchParams({ tab: 'approvals' }, { replace: true });
    else setSearchParams({}, { replace: true });
  };

  const loadStaff = useCallback(async () => {
    const [openRows, mineRows] = await Promise.all([
      shiftMarketplaceApi.getRequests({ status: 'Open' }),
      shiftMarketplaceApi.getRequests({ mine: true }),
    ]);
    const byId = new Map<number, ShiftMarketplaceRequest>();
    for (const r of [...openRows, ...mineRows]) byId.set(r.id, r);
    setStaffRows(Array.from(byId.values()));
  }, []);

  const loadApprovals = useCallback(async () => {
    if (!canApprove) {
      setApprovalRows([]);
      return;
    }
    const data = await shiftMarketplaceApi.getRequests({ approvals: true });
    setApprovalRows(data);
  }, [canApprove]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      if (mainTab === 'approvals') await loadApprovals();
      else await loadStaff();
    } catch (e: any) {
      setError(
        e?.response?.status === 403
          ? 'You do not have access to the shift marketplace.'
          : e?.response?.data?.message || 'Failed to load marketplace'
      );
      setStaffRows([]);
      setApprovalRows([]);
    } finally {
      setLoading(false);
    }
  }, [mainTab, loadStaff, loadApprovals]);

  useEffect(() => {
    load();
  }, [load]);

  const sections = useMemo(() => {
    const needsAction: ShiftMarketplaceRequest[] = [];
    const myOffers: ShiftMarketplaceRequest[] = [];
    const inProgress: ShiftMarketplaceRequest[] = [];

    const sortAction = (a: ShiftMarketplaceRequest, b: ShiftMarketplaceRequest) =>
      new Date(b.updatedAt || b.createdAt).getTime() -
      new Date(a.updatedAt || a.createdAt).getTime();

    if (!myId) {
      return {
        needsAction: staffRows
          .filter((r) => r.status === 'Open' && r.requestType === 'Giveaway')
          .sort(sortAction),
        myOffers: [] as ShiftMarketplaceRequest[],
        inProgress: [] as ShiftMarketplaceRequest[],
      };
    }

    for (const r of staffRows) {
      const isMine = r.offeredByEmployeeId === myId;
      const isSwapTarget =
        r.requestType === 'Swap' && r.requestedShift?.employeeId === myId;
      const isAccepter = r.acceptedByEmployeeId === myId;

      if (
        r.status === 'Open' &&
        !isMine &&
        (r.requestType === 'Giveaway' || isSwapTarget)
      ) {
        needsAction.push(r);
        continue;
      }

      if (isMine && ACTIVE_MARKETPLACE_STATUSES.has(r.status)) {
        myOffers.push(r);
        continue;
      }

      if (isMine || isAccepter || isSwapTarget) {
        inProgress.push(r);
      }
    }

    return {
      needsAction: needsAction.sort(sortAction),
      myOffers: myOffers.sort(sortAction),
      inProgress: inProgress.sort(sortAction),
    };
  }, [staffRows, myId]);

  const pendingByShiftId = useMemo(() => {
    const map = new Map<number, ShiftMarketplaceRequest>();
    for (const r of staffRows) {
      if (!ACTIVE_MARKETPLACE_STATUSES.has(r.status)) continue;
      map.set(r.offeredShiftId, r);
      if (r.requestedShiftId) map.set(r.requestedShiftId, r);
    }
    return map;
  }, [staffRows]);

  const offerableShifts = useMemo(
    () => myShifts.filter((s) => isFutureOfferShift(s) && !pendingByShiftId.has(s.id)),
    [myShifts, pendingByShiftId]
  );

  const approvalPending = useMemo(
    () => approvalRows.filter((r) => r.status === 'PendingApproval'),
    [approvalRows]
  );
  const approvalHistory = useMemo(
    () =>
      approvalRows
        .filter((r) => r.status === 'Completed' || r.status === 'Rejected')
        .sort(
          (a, b) =>
            new Date(b.approvedAt || b.updatedAt || b.createdAt).getTime() -
            new Date(a.approvedAt || a.updatedAt || a.createdAt).getTime()
        ),
    [approvalRows]
  );
  const runAction = async (id: number, action: () => Promise<unknown>) => {
    try {
      setBusyId(id);
      setError(null);
      await action();
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Action failed');
    } finally {
      setBusyId(null);
    }
  };

  const openCreate = async (kind: 'Giveaway' | 'Swap') => {
    setCreateKind(kind);
    setSelectedOfferShiftId('');
    setSwapTargetId('');
    setPeerShifts([]);
    setCreateNotes('');
    setError(null);
    if (!myId) return;
    setLoadingMyShifts(true);
    try {
      const start = format(new Date(), 'yyyy-MM-dd');
      const end = format(addWeeks(new Date(), 4), 'yyyy-MM-dd');
      const shifts = await shiftApi.getAll({
        startDate: start,
        endDate: end,
        employeeId: myId,
        publishedOnly: true,
        bypassCache: true,
      });
      setMyShifts(shifts);
    } catch {
      setMyShifts([]);
      setError('Could not load your upcoming shifts.');
    } finally {
      setLoadingMyShifts(false);
    }
  };

  const loadPeersForShift = async (shiftId: number) => {
    const offered = myShifts.find((s) => s.id === shiftId);
    if (!offered) {
      setPeerShifts([]);
      return;
    }
    setLoadingPeers(true);
    try {
      const start = format(new Date(), 'yyyy-MM-dd');
      const end = format(addWeeks(new Date(), 4), 'yyyy-MM-dd');
      const all = await shiftApi.getAll({
        startDate: start,
        endDate: end,
        locationId: offered.locationId,
        publishedOnly: true,
        bypassCache: true,
      });
      setPeerShifts(
        all.filter(
          (s) =>
            s.employeeId !== myId &&
            s.locationId === offered.locationId &&
            parseLocalDateTime(s.startTime).getTime() > Date.now() &&
            !pendingByShiftId.has(s.id)
        )
      );
    } catch {
      setPeerShifts([]);
      setError('Could not load peer shifts for swap.');
    } finally {
      setLoadingPeers(false);
    }
  };

  const submitCreate = async () => {
    if (!selectedOfferShiftId) return;
    if (createKind === 'Swap' && !swapTargetId) return;
    setCreating(true);
    setError(null);
    try {
      await shiftMarketplaceApi.create({
        requestType: createKind === 'Swap' ? 'Swap' : 'Giveaway',
        offeredShiftId: Number(selectedOfferShiftId),
        requestedShiftId: createKind === 'Swap' ? Number(swapTargetId) : undefined,
        notes: trimmedMarketplaceComment(createNotes),
      });
      setCreateKind(null);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to create request');
    } finally {
      setCreating(false);
    }
  };

  const renderStaffCard = (r: ShiftMarketplaceRequest) => {
    const isMine = r.offeredByEmployeeId === myId;
    const isSwapCounterparty =
      r.requestType === 'Swap' && r.requestedShift?.employeeId === myId;
    const canAccept =
      r.status === 'Open' &&
      !isMine &&
      (r.requestType === 'Giveaway' || isSwapCounterparty);
    const canDecline = r.status === 'Open' && isSwapCounterparty;
    const canCancel =
      isMine &&
      (r.status === 'Open' || r.status === 'PendingApproval' || r.status === 'Accepted');

    return (
      <MarketplaceRequestDetails
        key={r.id}
        request={r}
        viewerEmployeeId={myId}
        statusOverride={marketplaceStatusMeta(r.status, {
          isResponse: !isMine && (r.acceptedByEmployeeId === myId || isSwapCounterparty),
          requestType: r.requestType,
        })}
        footerNote={`Updated ${formatCompanyDateTime(r.updatedAt || r.createdAt)}`}
        defaultExpanded={marketplaceDefaultExpanded(r.status)}
      >
        {(canAccept || canDecline || canCancel) && (
          <div className="space-y-2">
            {(canAccept || canDecline) && (
              <label className="block text-sm">
                <span className="text-neutral-600">Your comment (optional)</span>
                <textarea
                  className="mt-1 w-full border border-neutral-300 rounded-md px-3 py-2 text-sm"
                  rows={2}
                  maxLength={500}
                  value={actionNotes[r.id] ?? ''}
                  onChange={(e) =>
                    setActionNotes((prev) => ({ ...prev, [r.id]: e.target.value }))
                  }
                  placeholder="Add a comment if you want — leave blank to skip"
                />
              </label>
            )}
            <div className="flex flex-wrap gap-2">
              {canAccept && (
                <button
                  type="button"
                  disabled={busyId === r.id}
                  onClick={() =>
                    runAction(r.id, () =>
                      shiftMarketplaceApi.accept(r.id, trimmedMarketplaceComment(actionNotes[r.id]))
                    )
                  }
                  className="px-3 py-1.5 rounded-md bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-50"
                >
                  Accept
                </button>
              )}
              {canDecline && (
                <button
                  type="button"
                  disabled={busyId === r.id}
                  onClick={() =>
                    runAction(r.id, () =>
                      shiftMarketplaceApi.decline(r.id, trimmedMarketplaceComment(actionNotes[r.id]))
                    )
                  }
                  className="px-3 py-1.5 rounded-md border border-neutral-300 text-sm hover:bg-neutral-50 disabled:opacity-50"
                >
                  Decline
                </button>
              )}
              {canCancel && (
                <button
                  type="button"
                  disabled={busyId === r.id}
                  onClick={async () => {
                    if (await appConfirm('Cancel this marketplace request?', { danger: true, confirmLabel: 'Cancel' })) {
                      runAction(r.id, () => shiftMarketplaceApi.cancel(r.id));
                    }
                  }}
                  className="px-3 py-1.5 rounded-md border border-neutral-300 text-sm text-red-700 hover:bg-red-50 disabled:opacity-50"
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        )}
      </MarketplaceRequestDetails>
    );
  };

  const renderApprovalCard = (r: ShiftMarketplaceRequest, opts: { canAct: boolean; expand: boolean }) => {
    const footerNote =
      !opts.canAct && (r.approvedAt || r.updatedAt || r.approvedByName)
        ? r.status === 'Completed'
          ? `Approved${r.approvedByName ? ` by ${r.approvedByName}` : ''}${
              r.approvedAt || r.updatedAt
                ? ` · ${formatCompanyDateTime(r.approvedAt || r.updatedAt)}`
                : ''
            }`
          : r.status === 'Rejected'
            ? `Rejected${r.approvedByName ? ` by ${r.approvedByName}` : ''} · ${formatCompanyDateTime(
                r.approvedAt || r.updatedAt
              )}`
            : undefined
        : undefined;

    return (
      <MarketplaceRequestDetails
        key={r.id}
        request={r}
        viewerEmployeeId={myId}
        footerNote={footerNote}
        defaultExpanded={opts.expand}
      >
        {opts.canAct && (
          <div className="space-y-2">
            <label className="block text-sm">
              <span className="text-neutral-600">Your comment (optional)</span>
              <textarea
                className="mt-1 w-full border border-neutral-300 rounded-md px-3 py-2 text-sm"
                rows={2}
                maxLength={500}
                value={approvalNotes[r.id] ?? ''}
                onChange={(e) =>
                  setApprovalNotes((prev) => ({ ...prev, [r.id]: e.target.value }))
                }
                placeholder="Add a comment if you want — leave blank to skip"
              />
            </label>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={busyId === r.id}
                onClick={async () => {
                  if (await appConfirm('Approve and update the roster?', { confirmLabel: 'Approve' })) {
                    runAction(r.id, () =>
                      shiftMarketplaceApi.approve(
                        r.id,
                        trimmedMarketplaceComment(approvalNotes[r.id])
                      )
                    );
                  }
                }}
                className="px-3 py-1.5 rounded-md bg-green-600 text-white text-sm hover:bg-green-700 disabled:opacity-50"
              >
                Approve
              </button>
              <button
                type="button"
                disabled={busyId === r.id}
                onClick={async () => {
                  if (await appConfirm('Reject this marketplace request?', { danger: true, confirmLabel: 'Reject' })) {
                    runAction(r.id, () =>
                      shiftMarketplaceApi.reject(
                        r.id,
                        trimmedMarketplaceComment(approvalNotes[r.id])
                      )
                    );
                  }
                }}
                className="px-3 py-1.5 rounded-md border border-neutral-300 text-sm text-red-700 hover:bg-red-50 disabled:opacity-50"
              >
                Reject
              </button>
            </div>
          </div>
        )}
      </MarketplaceRequestDetails>
    );
  };

  const section = (title: string, rows: ShiftMarketplaceRequest[], empty: string) => (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold text-neutral-700 uppercase tracking-wide">{title}</h2>
      {rows.length === 0 ? (
        <p className="text-sm text-neutral-500 py-2">{empty}</p>
      ) : (
        <div className="space-y-3">{rows.map(renderStaffCard)}</div>
      )}
    </section>
  );

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-neutral-800">Marketplace</h1>
          <p className="text-sm text-neutral-500 mt-1">
            Covers and swaps in one place. Act on requests, track yours, and (if you schedule) approve
            roster changes.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/my-schedules"
            className="px-3 py-2 rounded-lg border border-neutral-300 text-sm text-neutral-700 hover:bg-neutral-50"
          >
            My Schedules
          </Link>
          <button
            type="button"
            onClick={load}
            className="p-2 rounded-lg bg-neutral-100 hover:bg-neutral-200"
            aria-label="Refresh"
          >
            <RefreshCw className={`h-5 w-5 text-blue-600 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {(canApprove || canPropose) && canApprove && (
        <div className="flex gap-2 border-b border-neutral-200">
          {canPropose && (
            <button
              type="button"
              onClick={() => selectMainTab('forme')}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                mainTab === 'forme'
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-neutral-500'
              }`}
            >
              For me
            </button>
          )}
          <button
            type="button"
            onClick={() => selectMainTab('approvals')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              mainTab === 'approvals'
                ? 'border-blue-600 text-blue-700'
                : 'border-transparent text-neutral-500'
            }`}
          >
            Approvals
          </button>
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {mainTab === 'forme' && canPropose && (
        <>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => void openCreate('Giveaway')}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
            >
              <HandHelping className="h-4 w-4" />
              Request shift cover
            </button>
            <button
              type="button"
              onClick={() => void openCreate('Swap')}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-neutral-300 text-sm font-medium text-neutral-800 hover:bg-neutral-50"
            >
              <ArrowLeftRight className="h-4 w-4" />
              Propose shift swap
            </button>
          </div>

          {loading ? (
            <div className="text-neutral-500 py-12 text-center">Loading…</div>
          ) : (
            <div className="space-y-8">
              {section(
                'Requests from Colleagues',
                sections.needsAction,
                'Nothing waiting for you right now.'
              )}
              {section(
                'My requests',
                sections.myOffers,
                'You have no active cover or swap requests. Start from the buttons above or from My Schedules.'
              )}
              {section(
                'In progress & done',
                sections.inProgress,
                'No other marketplace activity yet.'
              )}
            </div>
          )}
        </>
      )}

      {mainTab === 'approvals' && canApprove && (
        <>
          {loading ? (
            <div className="text-neutral-500 py-12 text-center">Loading…</div>
          ) : approvalPending.length === 0 && approvalHistory.length === 0 ? (
            <div className="text-neutral-500 py-12 text-center">
              No marketplace approvals yet.
            </div>
          ) : (
            <div className="space-y-8">
              <section className="space-y-3">
                <h2 className="text-sm font-semibold text-neutral-700 uppercase tracking-wide">
                  Pending
                </h2>
                {approvalPending.length === 0 ? (
                  <p className="text-sm text-neutral-500 py-2">No pending marketplace approvals.</p>
                ) : (
                  <div className="space-y-3">
                    {approvalPending.map((r) =>
                      renderApprovalCard(r, { canAct: true, expand: true })
                    )}
                  </div>
                )}
              </section>
              {approvalHistory.length > 0 && (
                <section className="space-y-3">
                  <h2 className="text-sm font-semibold text-neutral-700 uppercase tracking-wide">
                    History
                  </h2>
                  <div className="space-y-3">
                    {approvalHistory.map((r) =>
                      renderApprovalCard(r, { canAct: false, expand: false })
                    )}
                  </div>
                </section>
              )}
            </div>
          )}
        </>
      )}

      {createKind && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-5 space-y-4">
            <div className="flex items-start justify-between gap-2">
              <h3 className="text-lg font-semibold text-neutral-900">
                {createKind === 'Swap' ? 'Propose shift swap' : 'Request shift cover'}
              </h3>
              <button
                type="button"
                onClick={() => setCreateKind(null)}
                className="rounded p-1 hover:bg-neutral-100"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm text-neutral-600">
              {createKind === 'Swap'
                ? 'Choose one of your upcoming shifts, then the colleague shift to swap with. A manager must approve before the roster changes.'
                : 'Choose one of your upcoming shifts to offer for cover. A colleague can accept it, then a manager must approve.'}
            </p>

            {loadingMyShifts ? (
              <p className="text-sm text-neutral-500">Loading your shifts…</p>
            ) : (
              <label className="block text-sm space-y-1">
                <span className="text-neutral-700 font-medium">Your shift</span>
                <select
                  className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm"
                  value={selectedOfferShiftId}
                  onChange={(e) => {
                    const id = e.target.value ? Number(e.target.value) : '';
                    setSelectedOfferShiftId(id);
                    setSwapTargetId('');
                    if (createKind === 'Swap' && id) void loadPeersForShift(id);
                  }}
                >
                  <option value="">Select a shift…</option>
                  {offerableShifts.map((s) => (
                    <option key={s.id} value={s.id}>
                      {shiftOptionLabel(s)}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {createKind === 'Swap' && selectedOfferShiftId !== '' && (
              <label className="block text-sm space-y-1">
                <span className="text-neutral-700 font-medium">Swap with</span>
                {loadingPeers ? (
                  <p className="text-neutral-500">Loading peer shifts…</p>
                ) : (
                  <select
                    className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm"
                    value={swapTargetId}
                    onChange={(e) => setSwapTargetId(e.target.value ? Number(e.target.value) : '')}
                  >
                    <option value="">Select colleague shift…</option>
                    {peerShifts.map((s) => (
                      <option key={s.id} value={s.id}>
                        {shiftOptionLabel(s)}
                      </option>
                    ))}
                  </select>
                )}
              </label>
            )}

            <label className="block text-sm space-y-1">
              <span className="text-neutral-700 font-medium">Comment (optional)</span>
              <textarea
                className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm"
                rows={2}
                maxLength={500}
                value={createNotes}
                onChange={(e) => setCreateNotes(e.target.value)}
                placeholder={
                  createKind === 'Swap'
                    ? 'Why are you requesting this swap?'
                    : 'Why are you offering this shift for cover?'
                }
              />
            </label>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setCreateKind(null)}
                className="px-3 py-2 rounded-md border border-neutral-300 text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={
                  creating ||
                  !selectedOfferShiftId ||
                  (createKind === 'Swap' && !swapTargetId)
                }
                onClick={() => void submitCreate()}
                className="px-3 py-2 rounded-md bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-50"
              >
                {createKind === 'Swap' ? 'Propose swap' : 'Request cover'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShiftMarketplace;
